import { _ } from '@ag-grid-community/core'
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core'
import {
  BasePanelComponent,
  PanelConfig,
  PanelMetadata,
} from '@helios/panel-base'
import { BooleanInput, booleanInputSetter } from '@northfork/basics'
import { LoggingService } from '@northfork/infra'
import {
  ComponentContainer,
  ComponentItem,
  ComponentItemConfig,
  DragSource,
  EventEmitter as GLEventEmitter,
  GoldenLayout,
  LayoutConfig,
  ResolvedComponentItemConfig,
  ResolvedLayoutConfig,
} from 'golden-layout'
import { v4 as UUID } from 'uuid'
import { LayoutManagerService } from './layout-manager.service'
import { createLockedLayout } from './lock-layout'
import {
  NotFoundComponent,
  NotFoundComponentConfig,
} from './not-found/not-found.component'

/**
 * Holds some relevant goldenLayout data for a panel currently in the layout
 */
interface LayoutMetadata {
  container: ComponentContainer
  componentRef: ComponentRef<BasePanelComponent>
  item: ComponentItem | undefined
}

@Component({
  selector: 'hs-layout-manager',
  template: '',
  styleUrls: ['./layout-manager.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutManagerComponent implements OnDestroy {
  private dragSources: Array<DragSource> = []

  readonly goldenLayout = new GoldenLayout(this._elRef.nativeElement)

  @Input() set panels(panels: PanelConfig[]) {
    this.layoutManagerService.setPanels([...panels, NotFoundComponentConfig])
  }

  private layoutSet = false
  @Input() get layout() {
    return this.goldenLayout.saveLayout() as unknown as LayoutConfig
  }
  set layout(l: LayoutConfig | undefined) {
    if (!l) return
    this.loadLayout(l)
  }

  private _locked = false
  @Input() set locked(l: BooleanInput) {
    const locked = booleanInputSetter(l)
    if (locked != this._locked) {
      this._locked = locked
      this.setLayoutLocked(booleanInputSetter(locked))
    }
  }
  @Output() layoutChanged = new EventEmitter<LayoutConfig>()
  @Output()
  panelFocused = new EventEmitter<string>()
  @Output()
  stackDestroyed = new EventEmitter<string>()
  @Output()
  stackActiveTabUpdate = new EventEmitter<{
    stackId: string
    panelKey: string
  }>()

  panelAdded$ = new EventEmitter<string>()
  panelRemoved$ = new EventEmitter<string>()

  private readonly panelIndex = new Map<
    string, //panelKey
    LayoutMetadata
  >()

  private readonly containerIndex = new Map<
    ComponentContainer,
    string //panelKey
  >()

  private isInitLayout = true

  /**
   * Ignore excessive change events, such as resizing.
   * Debounce dispatching the event.
   */
  private readonly emitStateChanged = _.debounce(
    (layout: ResolvedLayoutConfig | LayoutConfig) => {
      this.layoutChanged.emit(layout as LayoutConfig)
    },
    500,
  )

  constructor(
    private readonly _elRef: ElementRef<HTMLElement>,
    private readonly appRef: ApplicationRef,
    private readonly layoutManagerService: LayoutManagerService,
    private readonly loggingService: LoggingService,
  ) {
    this.layoutManagerService.setPanels([NotFoundComponentConfig])
    this.initializeGoldenLayout()
  }

  ngOnDestroy() {
    this._cleanup()
  }

  loadLayout(layout: LayoutConfig) {
    this.goldenLayout.loadLayout(layout)
    this.layoutSet = true
  }

  addPanel(panelMetadata: PanelMetadata) {
    const panelKey = panelMetadata.key

    const itemConfig: ComponentItemConfig = {
      type: 'component',
      componentType: panelKey,
      title: panelMetadata.title,
      reorderEnabled: true,
    }

    //add the panel to the layout - results in:
    // -  handleBindComponentEvent which creates the panel and attaches it to the layout container
    // -  panelIndex being populated
    const item = this.goldenLayout.newItem(itemConfig) as ComponentItem

    //attach ComponentItem to LayoutMetadata for the panel
    const layoutMeta = this.panelIndex.get(panelKey)
    if (!layoutMeta) throw new Error('cannot find panel in index') //this shouldn't ever happen
    layoutMeta.item = item

    this.focusPanel(panelKey)
  }

  removePanel(panelMetadata: PanelMetadata) {
    this.panelIndex.get(panelMetadata.key)?.container.close()
    //panel will be removed and cleaned up in handleUnbindComponent
  }

  isPanelAdded(panelKey: string | undefined): boolean {
    if (!panelKey) return false
    return this.panelIndex.has(panelKey)
  }

  focusPanel(panelKey: string | undefined) {
    if (!panelKey) return

    setTimeout(() => this.panelIndex.get(panelKey)?.container.focus())
  }

  setLayoutLocked(locked: boolean) {
    this.layout = createLockedLayout(
      this.layout as unknown as ResolvedLayoutConfig,
      locked,
    )
  }

  /**
   * @internal for test access
   */
  readonly _onStateChanged = () => {
    if (!this.isInitialized()) return

    // The first time this is triggered is during the load of the layout
    if (this.isInitLayout) {
      this.isInitLayout = false
      return
    }

    this.emitStateChanged(this.layout)
  }

  readonly _onFocusChanged = (event: GLEventEmitter.BubblingEvent) => {
    const item: ComponentItem = event.target as ComponentItem
    const panelKey = item.componentType as string
    this.panelFocused.emit(panelKey)
  }

  readonly _itemCreated = (event: GLEventEmitter.BubblingEvent) => {
    const item = event.target as ComponentItem
    if (item.isStack) {
      item.id = UUID()
    }
  }

  readonly _itemDestroyed = (event: GLEventEmitter.BubblingEvent) => {
    const item = event.target as ComponentItem
    if (item.isStack && item.id != null) {
      this.stackDestroyed.emit(item.id)
    }
  }

  readonly _activeContentItemChanged = (item: ComponentItem) => {
    const parent = item.parent
    if (parent != null)
      this.stackActiveTabUpdate.emit({
        stackId: parent.id,
        panelKey: item.componentType as string,
      })
  }

  /**
   * Called when GoldenLayout needs to add a panel (whether by setting initial layout, dragSource, or programmatically adding a panel).
   * This creates the _actual_ Helios Panel and attaches it to the container.
   * This is the source of truth of whether a panel is in the layout or not.
   *
   * @param container
   * @param itemConfig
   * @returns
   */
  private handleBindComponentEvent(
    container: ComponentContainer,
    itemConfig: ResolvedComponentItemConfig,
  ) {
    const panelKey = itemConfig.componentType as string

    let componentRef
    try {
      componentRef = this.layoutManagerService.createPanelComponent(
        panelKey,
        container,
      )
    } catch (e: unknown) {
      componentRef = this.layoutManagerService.createPanelComponent(
        NotFoundComponentConfig.key,
        container,
      )
      const notFound = componentRef.instance as NotFoundComponent
      notFound.title = itemConfig.title

      console.error(e)
      this.loggingService.error(
        `The following panel failed to be found: ${panelKey}.`,
      )
    }
    this.appRef.attachView(componentRef.hostView)

    const domElem = (componentRef.hostView as EmbeddedViewRef<unknown>)
      .rootNodes[0] as HTMLElement
    container.element.appendChild(domElem)

    const layoutMeta: LayoutMetadata = {
      componentRef: componentRef,
      container: container,
      item: undefined, // will be set in addPanel
    }

    //link up containers, panelKey, componentRef
    this.panelIndex.set(panelKey, layoutMeta)
    this.containerIndex.set(container, panelKey)

    //let people know
    this.panelAdded$.emit(panelKey)

    //GL doesn't change focus when tab nav focuses an input - fix that here
    container.element.addEventListener('focusin', () => {
      this.focusPanel(panelKey)
    })

    return {
      component: componentRef.instance,
      virtual: false,
    }
  }

  private handleUnbindComponentEvent(container: ComponentContainer) {
    const panelKey = this.containerIndex.get(container)

    if (panelKey === undefined) {
      throw new Error('Could not unbind component. PanelKey not found')
    }
    const componentRef = this.panelIndex.get(panelKey)?.componentRef
    if (componentRef) {
      this.appRef.detachView(componentRef.hostView)
      componentRef.destroy()
    }

    this.containerIndex.delete(container)
    this.panelIndex.delete(panelKey)

    this.panelRemoved$.emit(panelKey)
  }

  private initializeGoldenLayout() {
    this.goldenLayout.resizeWithContainerAutomatically = true //just like it says, it follows the size of it's container.  resizing is by default debounced to 100ms
    this.goldenLayout.bindComponentEvent = (container, itemConfig) =>
      this.handleBindComponentEvent(container, itemConfig)
    this.goldenLayout.unbindComponentEvent = container =>
      this.handleUnbindComponentEvent(container)

    /**
     * Ignore excessive change events, such as resizing.
     * Debounce dispatching the event.
     */
    this.goldenLayout.on(
      'activeContentItemChanged',
      this._activeContentItemChanged,
    )
    this.goldenLayout.on('itemCreated', this._itemCreated)
    this.goldenLayout.on('itemDestroyed', this._itemDestroyed)
    this.goldenLayout.on('stateChanged', this._onStateChanged)
    this.goldenLayout.on('focus', this._onFocusChanged)
  }

  isInitialized(): boolean {
    if (!this.goldenLayout.isInitialised) return false

    //every popout needs to be initialized
    return this.goldenLayout.openPopouts.every(popout => {
      /**
       * goldenLayout.isInitialised does not take into consideration popout state. New popouts within the layout
       * can be unitialized and the goldenLayout.isInitialised will still reflect true. Additionally,
       * browserPopout.isInitialised (seems to) always returns false even after it has been initialized, so you must
       * use browserPopout.getGlInstance().isInitialised to check initialization on popouts.
       */
      const browserPopout = popout
      return (
        !!browserPopout.getGlInstance() &&
        browserPopout.getGlInstance().isInitialised
      )
    })
  }

  /**
   * Drag Sources are the PanelCardComponent's element
   *
   * @param panelKey
   * @param element  the PanelCardComponent
   * @param panelTitle
   * @returns
   */
  loadDragSource(panelKey: string, element: ElementRef, panelTitle: string) {
    if (this.layoutSet)
      console.error(
        'Drag source created after layout already set. ' +
          'Whenever a new drag source is loaded all of the components in the layout get recreated. ' +
          'Loading them prior to loading the layout will prevent this behavior.',
      )

    const dragSource = this.goldenLayout.newDragSource(
      element.nativeElement,
      panelKey,
      undefined,
      panelTitle,
    )

    this.dragSources = [...this.dragSources, dragSource]

    return dragSource
  }

  removeDragSources(dragSources: DragSource[]): void {
    this.dragSources = this.dragSources.filter(x => {
      return !dragSources.includes(x)
    })

    dragSources.forEach(source => {
      this.goldenLayout?.removeDragSource(source)
    })
  }

  _cleanup() {
    this.dragSources.forEach(source => {
      this.goldenLayout?.removeDragSource(source)
    })

    this.goldenLayout.off(
      'activeContentItemChanged',
      this._activeContentItemChanged,
    )
    this.goldenLayout.off('itemCreated', this._itemCreated)
    this.goldenLayout.off('itemDestroyed', this._itemDestroyed)
    this.goldenLayout.off('stateChanged', this._onStateChanged)
    this.goldenLayout.off('focus', this._onFocusChanged)

    this.goldenLayout.destroy()
  }

  /**
   * Test access only!
   *
   * @internal
   */
  _getLayoutMetadata(panelKey: string): LayoutMetadata | undefined {
    return this.panelIndex.get(panelKey)
  }
}
